import type React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Lightbulb, Pause, Play } from 'lucide-react';
import { useAudioPlayer } from '@/components/audio-player';
import type { AbDialogPayload } from '@/components/review/review-context';
import { useReview } from '@/components/review/review-context';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';

interface AbDialogProps {
  payload: AbDialogPayload | null;
  onClose: () => void;
}

/**
 * SPEC §6/§11 — after a regenerate: play current vs new, then keep one.
 *
 * `POST /assets/:id/regenerate` records the new take but leaves `currentTakeId`
 * where it was, so nothing is adopted until this dialog calls `select-take` —
 * which both buttons do explicitly ("Keep current" re-selects the earlier take,
 * which the API allows). Dismissing the dialog therefore changes nothing.
 */
export function AbDialog({ payload, onClose }: AbDialogProps): React.JSX.Element {
  const player = useAudioPlayer();
  const { applyAsset, openOverride } = useReview();

  const select = useMutation({
    mutationFn: (takeId: string) => {
      if (!payload) throw new Error('No take to select');
      return api.assets.selectTake(payload.assetId, { takeId });
    },
    onSuccess: (result, takeId) => {
      if (payload) applyAsset(payload.rowId, result.asset);
      player.stop();
      toast.success(takeId === payload?.newTakeId ? 'Kept the new take' : 'Kept the current take');
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const clip = (label: string, key: string, url: string | null): React.JSX.Element => {
    const playing = player.isPlaying(key);
    return (
      <div className="flex items-center justify-between rounded-md border p-3">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-muted-foreground">
            {url ? 'ready to play' : 'no earlier take for this asset'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!url}
          aria-label={`${playing ? 'Pause' : 'Play'} ${label}`}
          onClick={() => {
            if (url) player.toggle(key, url);
          }}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {playing ? 'Pause' : 'Play'}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={payload !== null}
      onOpenChange={(open) => {
        if (!open) {
          player.stop();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Compare takes — {payload?.language} audio</DialogTitle>
          <DialogDescription>
            Nothing has changed yet. Listen to both and keep the one you want; the approved take is
            untouched either way.
          </DialogDescription>
        </DialogHeader>

        {payload ? (
          <div className="space-y-2">
            {clip('New take', `ab-new-${payload.newTakeId}`, payload.newTakeUrl)}
            {clip(
              'Current take',
              `ab-old-${payload.previousTakeId ?? 'none'}`,
              payload.previousTakeUrl,
            )}
            {payload.suggestOverride ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/5 p-3 text-sm">
                <Lightbulb className="mt-0.5 h-4 w-4 text-amber-600" aria-hidden />
                <div>
                  <p className="font-medium">Still not right after three tries?</p>
                  <p className="text-muted-foreground">
                    Spell the tricky word out phonetically in the pronunciation override — the export
                    always keeps the original text.
                  </p>
                  <Button
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => {
                      openOverride(payload.rowId);
                      player.stop();
                      onClose();
                    }}
                  >
                    Fix pronunciation
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            disabled={select.isPending || !payload?.previousTakeId}
            onClick={() => {
              if (payload?.previousTakeId) select.mutate(payload.previousTakeId);
            }}
          >
            Keep current
          </Button>
          <Button
            disabled={select.isPending}
            onClick={() => {
              if (payload) select.mutate(payload.newTakeId);
            }}
          >
            {select.isPending ? 'Saving…' : 'Keep new'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
